import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { resolvePlanTier } from '@/shared/ui';
import { agencyContext } from '@pages/authenticated/agency-pages/AgenciesContext';
import { usePlans } from '@features/onboarding/hooks/use-plans';
import {
  formatTrialDate, trialNoticeLevel, type NoticeLevel, type TrialKind,
} from '@/shared/utils/trial-quota';
import { agencyTypeToSegment } from '../components/agencyTypeToSegment';
import { trialPlanOf } from '../components/plan/trial-plan';
import type { TrialNoticeModel } from '../components/trial/TrialQuotaNoticeView';
import { useSeatUsage } from './useSeatUsage';
import { useSubscriptionDetail } from './useSubscriptionDetail';

/** The free-period ceilings, used only when a 402 arrives before usage loads. */
const FALLBACK_TOTAL: Record<TrialKind, number> = { interaction: 200, partnershipSearch: 3 };
const USAGE_KEY = { interaction: 'interaction', partnershipSearch: 'partnership_search' } as const;

/**
 * Writes the activation price the way the customer reads it.
 *
 * @param cents - What activating charges today, or null.
 * @param locale - BCP-47 tag.
 * @returns "R$ 454,90", or null when unknown.
 */
export function formatActivationPrice(cents: number | null | undefined, locale: string): string | null {
  if (typeof cents !== 'number' || cents <= 0) return null;
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

/**
 * The trial notice an agency has earned for one quota, or null.
 *
 * `forceBlock` is for the 402 `trial_quota_exhausted` answer: the server has
 * spoken, so the block shows even if the usage read is stale or missing.
 *
 * @param agencyId - The agency identifier.
 * @param kind - Which quota.
 * @param forceBlock - Show the block regardless of the usage figures.
 * @returns The model to render, or null when there is nothing to say.
 */
export function useTrialQuotaNotice(
  agencyId: string, kind: TrialKind, forceBlock = false,
): TrialNoticeModel | null {
  const { t, i18n } = useTranslation('features/pricing');
  const agency = agencyContext.useStore((state) => state.agency);
  const { data: usage } = useSeatUsage(agencyId);
  const { data: subscription } = useSubscriptionDetail(agencyId);
  const { plans } = usePlans({ defaultSegment: agencyTypeToSegment(agency?.type) });

  return useMemo(() => {
    const level: NoticeLevel = forceBlock ? 'block' : trialNoticeLevel(usage, kind);
    if (level === 'none') return null;
    const trialPlan = trialPlanOf(usage, plans ?? [], t);
    const source = usage?.[USAGE_KEY[kind]];
    return {
      kind,
      level,
      used: source?.used ?? FALLBACK_TOTAL[kind],
      total: source?.total ?? FALLBACK_TOTAL[kind],
      planName: trialPlan?.name ?? t(`subscription.trial.tier.${resolvePlanTier(usage?.plan_tier)}`),
      planQuota: trialPlan?.quotas[kind] ?? null,
      priceLabel: formatActivationPrice(subscription?.billing?.price_cents, i18n.language),
      waitDate: formatTrialDate(usage?.trial_ends_at, i18n.language),
    };
  }, [usage, plans, subscription, kind, forceBlock, t, i18n.language]);
}
